/**
 * Flow Queuing System — the enroll-queue context's TYPE surface, extracted
 * verbatim from `EnrollQueueContext.tsx` (F9 god-context cut 1 of 4). Pure
 * move: every declaration below is byte-identical to its previous home,
 * including doc comments, and `EnrollQueueContext.tsx` re-exports the public
 * ones so no consumer's import path changed.
 *
 * `PatchQueueItemError` is the one non-type member of this module — it lives
 * here because it is part of the same contract (`selectInputOption` is
 * documented against its `.status`) and moving it kept the cut a single
 * contiguous range.
 */
import type { QueueResponse, ItemPatch, InputFieldOptions, SenderOption, BulkAddResponse, StagedSendConfig } from '@shared/flow/enrollment';
import type { ActivePlayType, PlayOption } from '@shared/flow/play-catalog';
import type { GongFlowSummary } from '@shared/flow/gong-flow-catalog';
import type { StepOverrideDraft, StepOverrideSnapshot } from './state/step-override-drafts';

/**
 * Docked sidebar surfaces (U5 nav-rail restructure; originally U6's two-tab
 * bar). Internal ids are stable even where the rail's DISPLAY label differs
 * (see the approved prototype, docs/flow/sidebar-redesign-prototype.html):
 *  - `'queue'` — the pre-existing pending-enrollments view; rail label "Plays";
 *    default landing surface (was `'todos'` — see below).
 *  - `'requests'` — the live feed of the acting user's fired instant actions
 *    (RequestsTab.tsx; QA round 2, R3 — the feed is executions only,
 *    queued-but-not-yet-sent workflow enrollments no longer appear here);
 *    rail label "Actions" (R5) — the component/type name stays `requests`
 *    on purpose (internal id vs. user-facing rename only).
 *  - (Calendar surface split to the `flow-calendar` branch — out of scope: day
 *    view; see plan's Scope Boundaries).
 *
 * `'todos'` (the ranked cross-contact NBA list, R1-R4/R7, originally the
 * default landing tab per R5) was removed 2026-08-18 — the frontend surface
 * was unmounted as dead weight; the server stack it read from stays live
 * because the `list_my_todos` MCP tool still consumes it (see
 * `src/services/flow/todos.ts`, `GET /api/flow/todos`).
 *
 *  - Chat is NOT a dock tab. It has its own panel (`shell-next/panels/`),
 *    opened by `openChat()` below, so chat and the dock are independent
 *    surfaces and either can be open without the other.
 *  - `'customInvite'` — the Custom Invite stopgap panel (email + name →
 *    event-invite campaign → send; `CustomInvitePanel.tsx`); rail label
 *    "Custom Invite" (plan `2026-09-23-custom-invite-stopgap-plan.md`, U4/K5).
 *  - `'notes'` — the Account/Contact Notes pane (`shell-next/notes/`); rail
 *    label "Notes". The tab only renders when `useNotesEnabled()` is on.
 */
export type SidebarTab = 'queue' | 'requests' | 'customInvite' | 'notes';

/**
 * Fired when a Requests-detail "View on contact →" jump (RequestDetail.tsx)
 * wants a contact row highlighted in whatever contact table happens to be
 * mounted. `nonce` increments on every jump so the SAME contactId can be
 * jumped to twice in a row and still notify subscribers each time (a bare
 * contactId change wouldn't re-fire if the id were unchanged from the last
 * jump).
 *
 * QA round 2 (R4, reported bug): the jump used to have ZERO fallback when no
 * contact table was mounted to consume it — the signal fired into the void,
 * no scroll, no toast, nothing. It's now handled at THIS always-mounted
 * layer via `registerContactJumpHandler` below: a mounted ContactFlowTable
 * registers a handler that attempts the scroll+flash and reports whether it
 * found the row.
 *
 * Q4 (QA round 3, reported bug): a toast was still a dead end — "View on
 * contact" needs to work from ANYWHERE in the product, not just report that
 * it couldn't. When NO handler is registered at all (no contact table
 * mounted anywhere), this provider now NAVIGATES to the Flow contacts view
 * focused on that contact (`contactsInCommandCenter`) instead of toasting —
 * the global `ContactFlowTable` mount reads the resulting `?contact=` param
 * on mount and reuses this same scroll+flash path independently (see that
 * component's own URL-param effect). The toast is now reserved for the case
 * a handler WAS registered (a table is genuinely mounted and scanned its
 * currently loaded rows) but still reported "not found" — there's nothing
 * further to navigate to in that case.
 */
export interface ContactJumpRequest {
  contactId: string;
  nonce: number;
}

/**
 * A mounted contact table's jump handler: given a contactId, attempt to
 * scroll it into view + flash-highlight it, and return whether the row was
 * actually found. Registered by `ContactFlowTable` on mount, unregistered on
 * unmount (see `registerContactJumpHandler`'s doc comment below).
 */
export type ContactJumpHandler = (contactId: string) => boolean;

/**
 * A Todos → Plays deep-link's flash target (U7, R4 Separate model): a queued
 * todo doesn't edit inline — clicking it opens the Plays surface and asks the
 * queue body to scroll the matching contact's row into view and flash it (the
 * prototype's `qflash`). `nonce` increments per request so flashing the SAME
 * contact twice in a row still re-fires the effect (a bare contactId wouldn't
 * change) — mirrors `ContactJumpRequest`'s nonce rationale. If the contact no
 * longer has a queue row (stale todos cache), the queue body finds no match
 * and simply does nothing — the deep-link degrades to "just switched to
 * Plays", no flash, no crash (plan edge case).
 */
export interface QueueFlashRequest {
  contactId: string;
  nonce: number;
  /**
   * "Browse all Gong flows" enqueue-then-drill-in follow-up (2026-08-21) —
   * when true, the flashed contact's `UnassignedRow` reveals its own
   * "Assign to play…" picker AND that picker's browse panel, both without
   * any further click. `undefined`/`false` (every pre-existing producer —
   * there is none yet at that point in the file's history, but any FUTURE
   * plain scroll+flash caller) preserves the original scroll+CSS-flash-only
   * behavior. See `requestQueueFlash`'s doc comment on
   * `EnrollQueueContextValue` for the producer this was added for.
   */
  autoOpenPicker?: boolean;
  /**
   * Browse-overlay flow-pick auto-open-editor follow-up (2026-08-22) — when
   * true, the flashed contact's `AssignedRow` seeds its "Edit Config"
   * editor OPEN the instant it mounts, no second click needed. Distinct
   * from `autoOpenPicker` above (which targets the STILL-unassigned row's
   * own picker/browse panel, before an assignment exists) — this one
   * targets the row AFTER it has already been assigned. `undefined`/`false`
   * (every other producer) preserves the existing collapsed-by-default
   * `AssignedRow` behavior. Scoped narrowly to the Browse-overlay selection
   * path specifically (`PlayPicker.tsx`'s `handleAssign`'s `viaBrowse` flag,
   * threaded up through `QueueBody.tsx`'s `UnassignedRow`) — NOT the "Gong
   * Flows — suggested" quick-pick rows, and NOT any non-Gong play
   * assignment.
   */
  openEditorAfterAssign?: boolean;
}

/**
 * The display info `openSentRequestDetail`'s optimistic prepend needs for
 * ONE requested item — snapshotted in `sendMutation`'s `onMutate`, BEFORE
 * the send fires. It can't be looked up after the send settles: a
 * submitted item is filtered out of `GET /api/flow/queue` (see the poll
 * loop in `sendMutation.onSuccess`), so by the time the poll confirms
 * `remaining.length === 0` the item's contact/play fields are no longer
 * present in any queue response.
 */
export interface PendingSendItemSnapshot {
  contactId: string;
  // Nullable since Warm Intro plan U9 (R4): a warm_intro item may carry a
  // null contact_email (LinkedIn-sourced target). `RequestContactSchema.email`
  // is already nullable, so this passes straight through with no fallback.
  contactEmail: string | null;
  /**
   * This bead (U5, post-send toast) — the full display-identity chain
   * (`contactDisplayIdentity`: contact_name -> contact_email ->
   * contact_linkedin -> contact_id), not just the raw email above. Used for
   * the single-success toast's "Sent — <name> → <play>" copy; `contactEmail`
   * is kept as-is for `openSentRequestDetail`'s optimistic `RequestEntry`,
   * which predates this field and shouldn't be touched by this bead.
   */
  displayName: string;
  playName: string | null;
  /**
   * "View in Gong" toast follow-up (2026-08-20) — the item's `account_id`
   * (`EnrollmentItem.account_id`), needed alongside `contactId` to build the
   * Gong Engage deep-link (`gongEngageUrl`). Nullable for the same reason
   * `EnrollmentItem.account_id` itself is nullable (no account on the mirror
   * for this contact).
   */
  accountId: string | null;
  /**
   * The item's play type's `ui.has_step_overrides` capability (U5B — replaces
   * a `play_type_slug === 'gong_flow'` comparison; same `play` lookup this
   * loop already does for `playLabel`), needed so `finalizeSend`'s
   * single-success branch can special-case a step-override-capable play's
   * send with a "View in Gong" deep-link instead of the generic "View in
   * Actions" rail switch. `false` when the play no longer resolves in the
   * live catalog (matches `playLabel`'s own fallback path) or carries no
   * `ui` block — that case keeps "View in Actions".
   */
  hasStepOverrides: boolean;
}

/**
 * The identity-scoped facade over the browser-persisted staged play config
 * (`state/staged-play-config.ts`) that `BaseCopyEditor` reads/writes, and
 * that `PlayQueueCard`/`ContactQueueRow` read for their per-item copy
 * prefill — see `EnrollQueueContextValue.baseCopyDraftStore`'s doc comment
 * for the full rationale. Declared once here; `BaseCopyEditor`'s own
 * `BaseCopyDraft` value-shape interface stays where it is.
 *
 * `clearIfUnchanged` (the old server-ack path's `reseedIfUnchanged` analog)
 * is GONE with the save path it acknowledged — D8 Part B: nothing
 * round-trips, so there is no ack to clear on. Staged entries are pruned
 * only by the post-send reconciliation.
 */
/**
 * D12 Codex fix, findings #1–#3 — the per-item autosave PATCH body, with
 * BOTH halves independently optional.
 *
 * The optionality is load-bearing, not convenience. `PATCH
 * /api/flow/queue/items/:id` is a partial upsert (`'subject_draft' in body`,
 * src/api/flow-queue.ts), so an omitted field is left untouched — and a
 * sender-only pick that nonetheless sent `subject_draft` would persist the
 * row's currently-DISPLAYED base copy as a real per-item override, silently
 * detaching that row from every later global copy edit even though the rep
 * never touched copy (finding #3). The mirror holds for a copy-only edit: it
 * must not write `sender`, or nothing downstream can tell a pass-through
 * sender apart from a deliberate pick (finding #2).
 */
// A `type` rather than an `interface` on purpose: TS gives object-literal
// type aliases an implicit index signature, so this stays assignable to the
// `Record<string, unknown>` PATCH body `patchMutation` takes. An interface
// would need a cast at that boundary.
export type ItemAutosavePatch = {
  sender?: string;
  subject_draft?: string;
  body_draft?: string;
};

/**
 * D12 Codex fix, findings #1–#3 — per-HALF provenance for an
 * `ItemAutosavePatch`. "The rep picked a sender" and "the rep typed into
 * copy" are two INDEPENDENT facts about an item, tracked separately from the
 * field handler all the way to send-time materialization:
 *
 * - `sender: true` — this patch's `sender` is the rep's own deliberate pick,
 *   so a staged play-level sender must never override it.
 * - `copy: true` — this patch's `subject_draft`/`body_draft` are the rep's
 *   own typing, so staged play-level base copy must never override them.
 *
 * `false` means "written on the rep's behalf, not chosen": the single-option
 * sender auto-default, the connector-driven copy prefill, send-time
 * materialization and its rollback all carry `false`, so none of them can be
 * mistaken for an intentional override by whatever reads the entry next.
 *
 * Before this split, ONE signal (`EnrollmentItemEditor`'s `repEditedItemRef`,
 * plus an always-full `{sender, subject_draft, body_draft}` trio) stood in
 * for both facts at once — the single architectural root cause behind
 * findings #1, #2 and #3.
 */
export interface ItemAutosaveOwnership {
  sender: boolean;
  copy: boolean;
}

/**
 * D12 Codex fix, finding #5 — the role-derived default sender for one play
 * type, PLUS whether the query that produces it has actually resolved.
 *
 * A bare `string | null` conflated two genuinely different states: "this
 * play/rep combination has no default" and "the senders query hasn't
 * answered yet (or failed)". "Reset to base copy" computes `staged ??
 * default ?? null` and WRITES that value, so under the conflated shape a
 * reset fired while the query was still in flight wiped a perfectly valid
 * pre-existing `item.sender` by writing `null` over it. Consumers that
 * merely DISPLAY the default read `sender` and ignore `resolved`; the reset
 * path must honor `resolved` and fall back to the item's current sender
 * rather than destroying it.
 */
export interface DefaultSenderState {
  /** `true` once the senders query for this play type has produced data. */
  resolved: boolean;
  /** The role-derived default; `null` for "no default" AND while unresolved. */
  sender: string | null;
}

/**
 * D12 Codex round-2 fix, finding #6 — what `POST /api/flow/queue/send`
 * actually decided, surfaced to the CALLER instead of being consumed entirely
 * inside `sendMutation.onSuccess`.
 *
 * `sendItems` used to resolve `void`, so both send pipelines could only
 * distinguish two outcomes: the promise resolved (treated as "sent") or it
 * rejected (treated as "failed", and the one trigger for materialization
 * rollback). A SKIPPED item is neither: the HTTP response is a perfectly
 * successful 202 whose body says that particular row did not go out
 * (`SendSkip.reason` — `no_sender`, `already_sent`, `recent_invite`, …), and a
 * batch routinely mixes the two. Sender-only staging still writes the full
 * trio (an accepted round-1 tradeoff), so a skipped row kept its MATERIALIZED
 * copy/sender permanently — a silent override the rep never asked for, on an
 * item that was never sent.
 *
 * Both pipelines now roll back exactly `skippedIds` on an otherwise
 * successful send, which is the same treatment a rejection already gets, just
 * scoped to the ids the server actually refused.
 */
export interface SendItemsOutcome {
  /** `SendResponse.accepted` — ids admitted into the send queue. */
  acceptedIds: number[];
  /** `SendResponse.skipped`'s item ids — refused, so they need rollback. */
  skippedIds: number[];
}

export interface BaseCopyDraftStore {
  /**
   * D24 (qa-round3 plan, fix 1) — the effective-identity key this facade is
   * bound to (`${userId}:${actingAsSlackId ?? 'self'}`, `null` while
   * `/api/me` is unresolved), exposed so a consumer that DISPLAYS a staged
   * value can subscribe to it narrowly for itself
   * (`useStagedBaseCopy(store.identity, playKey)` and friends in
   * `state/staged-play-config.ts`).
   *
   * That is the whole point of this member: every field below is a WRITE.
   * Reads live entirely in the narrow per-field hooks keyed off this
   * identity, which is what keeps this object identity-stable across a
   * base-copy keystroke — so `EnrollQueueProvider` no longer re-renders (and
   * no longer re-mints the context value, and no longer fans out to all 16
   * `useEnrollQueue()` consumers) per character. A consumer that needs to
   * RE-RENDER on a staged change must therefore say so, per field, rather
   * than getting it for free from the whole app's re-render.
   */
  identity: string | null;
  setDraft: (playTypeId: number, playInstanceRef: string, draft: { subject: string; body: string }) => void;
  /**
   * D12 — the play-level staged SENDER, threaded through this same one prop
   * rather than a second facade: it is the same store, the same identity
   * keying, the same never-leaves-the-browser-before-Send lifecycle, and the
   * same consumers (`BaseCopyEditor` writes it; `PlayQueueCard`/
   * `ContactQueueRow` read it for their per-item `senderPrefill`).
   *
   * `undefined` means "the rep has not picked" — an entry exists only after
   * a deliberate pick, which is why no touched-gating is needed at the PLAY
   * level. The value is a `SenderSelectionSchema` selection (`'self'`/
   * `'owner'`/`'claydr'`/a team value), never a resolved email.
   */
  setBaseSender: (playTypeId: number, playInstanceRef: string, sender: string) => void;
  /**
   * D12 — "the rep hand-picked THIS row's sender", the per-item pin that
   * stops a staged global sender re-following it. Browser-persisted like the
   * rest of the staged config, so the pin (and the row's "Overridden" badge)
   * survives a reload/tab close; per-BROWSER by nature (see
   * `state/staged-play-config.ts`'s doc comment for the accepted caveat).
   * Read via `useStagedSenderTouched(identity)`, not off this facade.
   */
  markSenderTouched: (itemId: number) => void;
  /** "This row follows the play config again" — the reset path. */
  clearSenderTouched: (itemId: number) => void;
}

/**
 * The By Play card landing target (flow-contacts-bulk-send plan, U4) — see
 * `EnrollQueueContextValue.queuePlayLanding` for the full rationale and for
 * why this is a sibling of `QueueFlashRequest` rather than a field on it.
 *
 * `added`/`skipped` are the real per-contact outcome counts the add endpoint
 * reports (`BulkAddResponse`'s `added` / `skipped_already_queued` /
 * `skipped_no_email` tallies), never a guess — the banner states what actually
 * happened.
 *
 * `source` (flow-contacts-bulk-send QA round 3, D27) — an explicit
 * discriminator set by each producer (`'single'` from `ContactActionMenus`'
 * direct enroll, `'bulk'` from `EnrollQueueContext.bulkAdd`), consumed by
 * `QueueBody` to pick the assigned list's default grouping on landing. NEVER
 * inferred from `added === 1` — a bulk enroll of exactly one selected contact
 * is a real, distinct case, and `added` reflects server outcomes (a bulk of 5
 * where 4 were already queued reports `added: 1`), so an inference would be a
 * silent wrong answer. Deliberately absent from `QueueFlashRequest`: a
 * Browse-pick/open-request flash is not an enroll and must never switch
 * grouping.
 */
export interface QueuePlayLanding {
  playTypeId: number;
  playInstanceRef: string;
  added: number;
  skipped: number;
  source: 'single' | 'bulk';
  nonce: number;
}

export interface EnrollQueueContextValue {
  /** Queue groups, unassigned first (QueueResponseSchema invariant). */
  groups: QueueResponse['groups'];
  /** Live play catalog for the picker, in catalog order (no suggested-first, v0). */
  plays: PlayOption[];
  /**
   * R5e (PR #1312 review §7-3 / plan `2026-08-22-001` U8) — one entry per
   * active+enabled play TYPE, independent of `plays`' per-instance rows.
   * Fixes the picker bootstrap trap: `gong_flow`'s exposure deriver returns
   * `[]` on an empty mirror, so `plays` never carries a gong_flow row on a
   * fresh install — `PlayPicker`'s `gongCatalogEntry` reads this list
   * instead of scanning `plays`, so the Gong section (and its "Browse all
   * Gong flows →" affordance, the only way to populate that mirror) renders
   * regardless of instance count.
   */
  activePlayTypes: ActivePlayType[];
  /**
   * This rep's Tier-1 suggested-3 Gong flows (U12; GET /api/flow/gong/flows/
   * suggested) — fed into `PlayPicker`'s "Gong Flows — suggested" group,
   * separate from `plays` above (whose gong_flow entries — one per mirrored
   * flow, ~660 rows — are never rendered individually; see PlayPicker.tsx's
   * doc comment on its own `gongFlowsSuggested` prop). `[]` is the intended
   * cold-start render (Spencer 2026-08-19 — tiers 2/3 CUT), never backfilled.
   */
  gongFlowsSuggested: GongFlowSummary[];
  /**
   * Per-lead sender options for ONE play type — senders are play-type
   * config (play-senders.ts registry), not app config. Returns [] while
   * unloaded / for null / for a type no longer in the live catalog.
   */
  sendersFor: (playTypeId: number | null) => SenderOption[];
  /**
   * D12 point 3 — the ROLE-DERIVED default sender for ONE play type
   * (`SendersResponse.default_sender`, from the same `GET
   * /api/flow/queue/senders` response `sendersFor` reads). The client cannot
   * derive it: the default is a function of the rep's `User.userType`, which
   * no wire payload carries.
   *
   * Used as the play-level Sender field's CLEAN value, so a rep sees a
   * concrete sender rather than a "Select sender…" placeholder, and as the
   * fallback rung of "Reset to base copy"'s re-materialized sender
   * (`staged ?? default ?? null`).
   *
   * `sender` is `null` while unloaded, for a null play type, and for a
   * play/rep combination that genuinely has no default (event_invite for a
   * userType with no positive Flow role) — the field then really does show
   * its placeholder. D12 Codex fix #5: those cases are no longer
   * indistinguishable — see `DefaultSenderState.resolved`, which the reset
   * path MUST consult before it writes a null sender over a working one.
   */
  defaultSenderFor: (playTypeId: number | null) => DefaultSenderState;
  /**
   * Per-ITEM options for input fields the SERVER resolves per contact
   * (`GET /api/flow/queue/items/:id/input-options`, Warm Intro plan U8) —
   * today only warm_intro's connector list, which is why the keying is one
   * level down from `sendersFor`: senders are play-TYPE config (identical
   * for every item of that type), these are per-CONTACT and differ between
   * two items of the same play. Returns [] while unloaded, for an item that
   * isn't in the queue, and for every play type that declares no
   * `optionsSource` field (the endpoint answers `{ fields: [] }`).
   */
  inputOptionsFor: (itemId: number) => InputFieldOptions[];
  /** Total item count across every group — badge count for the handle/header. */
  totalCount: number;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  /**
   * The sole dock FAB's toggle (U9 — collapsed from the earlier Chat FAB +
   * Menu FAB pair). A plain open<->close that preserves `activeTab`
   * VERBATIM: closed -> `setIsOpen(true)` with
   * `activeTab` untouched; open -> `close()` (reused, not an inlined
   * `setIsOpen(false)` — coding standards' "Compose With" rule). Unlike the
   * pre-U9 contract, this never switches tabs itself — whichever tab was
   * last active (via `setActiveTab`/`openQueueTab`/etc.) is
   * exactly what reopens.
   */
  toggle: () => void;
  /**
   * Which docked-sidebar surface is active (U5 nav rail). Defaults to
   * `'queue'` (was `'todos'` — Todos was the default landing surface under
   * R5 until it was removed 2026-08-18).
   */
  activeTab: SidebarTab;
  setActiveTab: (tab: SidebarTab) => void;
  /**
   * Whether the right-edge dock is occupied: `'sidebar'` while it is open,
   * `'none'` when closed. Always DERIVED from `isOpen` during render, never its
   * own `useState` — a stored `rightOwner` plus `isOpen` would be two sources of
   * truth for one slot. Chat lives in its own panel, so it is never an owner
   * here. An OBSERVABILITY value for callers that want the slot's state without
   * re-deriving it.
   */
  rightOwner: 'none' | 'sidebar';
  /**
   * Opens the sidebar and switches it to the Queue tab (QA round 2, R2 —
   * spec reversal: a successful "Queue for Play" enroll must now open the
   * sidebar so the rep immediately sees the item land, undoing the earlier
   * "never force it open" ruling). Sibling to `openRequestDetail` below —
   * that one also drills into a specific request; this one has no per-item
   * target, so it's just open() + setActiveTab('queue').
   */
  openQueueTab: () => void;
  /**
   * Opens the Chat PANEL (`openPanel('chat')`). It never touches the dock:
   * chat has its own panel, so the dock's open state and `activeTab` stay as
   * they were. The entry point for evidence-link "Open chat" intents, e.g. a
   * completion toast's "View" action and the account-chat openers.
   */
  openChat: () => void;
  /**
   * Opens the dock on the Custom Invite surface (plan 2026-09-23). Mirror of
   * `openChat`: `setIsOpen(true) + setActiveTab('customInvite')`. Called by
   * the Plays tab's "Custom Invite" toolbar button (ContactFlowTable.tsx).
   */
  openCustomInvite: () => void;
  /**
   * Opens the dock on the Notes surface: `setIsOpen(true) + setActiveTab('notes')`,
   * the same shape as `openCustomInvite`. Callers that also pick a target go
   * through `useOpenNotes()` (shell-next/notes/), which composes this with
   * the Notes store's `openTarget`.
   */
  openNotes: () => void;
  /**
   * The current queue-row flash target (see `QueueFlashRequest`), consumed
   * by `QueueBody` to scroll+flash the matching row. `null` when no flash is
   * pending. Originally set by the (now-removed) Todos→Plays deep link
   * (`openQueueForContact`); left wired after that producer was removed
   * because it's a generic "flash this queue row" primitive `QueueBody`
   * already consumes. `requestQueueFlash` below is the first real producer —
   * the "Browse all Gong flows" contact-card follow-up (2026-08-21).
   */
  queueFlashRequest: QueueFlashRequest | null;
  /**
   * Clears `queueFlashRequest` once its flash has fully played out —
   * WITHOUT this, the request object is retained forever and re-flashes the
   * old contact every time the Plays surface remounts (tab away → back).
   * Nonce-guarded: a stale timer from an earlier flash can never clobber a
   * newer pending request. Called by `QueueBody` when the flash timer
   * completes; an interrupted flash (tab switch mid-animation) deliberately
   * stays un-consumed so returning to Plays replays it.
   */
  consumeQueueFlashRequest: (nonce: number) => void;
  /**
   * Sets `queueFlashRequest` for `contactId` — the generic producer side of
   * the primitive described above. `opts.autoOpenPicker: true` additionally
   * asks the flashed contact's `UnassignedRow` to reveal its own picker AND
   * that picker's browse panel with no further click (see
   * `QueueFlashRequest.autoOpenPicker`'s doc comment).
   *
   * First real caller: `ContactActionMenus.tsx`'s `WorkflowsDropdown`
   * `handleBrowseAll` — before this, enqueuing a contact via "Browse all
   * Gong flows" from the contact card landed the rep on a COLLAPSED queue
   * row; they then had to click "Assign to play…" on it, then click
   * "Browse all Gong flows" a second time inside that row's own picker to
   * reach the actual browse panel — three clicks to do one thing. Calling
   * this alongside `openQueueTab()` collapses that back to a single
   * continuous action: the sidebar opens already showing the row with its
   * picker and browse panel expanded.
   */
  requestQueueFlash: (contactId: string, opts?: { autoOpenPicker?: boolean; openEditorAfterAssign?: boolean }) => void;
  /**
   * The `RequestEntry.id` currently drilled into in the Requests tab (in-place
   * detail view), or `null` when showing the list. Cleared automatically by
   * `close()` is NOT done here on purpose — closing and reopening the sidebar
   * while on the Requests tab keeps the rep's place, matching the Queue tab's
   * own persistence (it never resets its scroll/edit state on close either).
   */
  selectedRequestId: string | null;
  /**
   * Opens the sidebar, switches to the Requests tab, and drills into
   * `requestId`'s detail view — the one call U7 (post-fire) and U8 (pill
   * deep-link, `requests-open` evidence-link intent) both need.
   */
  openRequestDetail: (requestId: string) => void;
  /** Back arrow in RequestDetail — returns to the Requests list, same tab. */
  closeRequestDetail: () => void;
  /**
   * Search seed for the Requests tab (flow-workflow-requests, "timestamps +
   * View all" — a contact's Recent Actions section shows at most 2
   * recent_request pills; a section-level "View all →" appears when a 3rd
   * exists). `RequestsTab` initializes its own local, EDITABLE search state
   * from this value and re-syncs whenever it changes — `null` means "no seed
   * pending". A fresh object is created on every `openRequestsForContact`
   * call (even for the same contact name twice in a row), so the effect that
   * consumes it always re-fires — mirrors `ContactJumpRequest`'s nonce
   * rationale above, but via object identity instead of a counter field
   * (there's nothing else here that needs the count itself).
   *
   * `contactId` (item #7 review finding): when present, RequestsTab scopes
   * the feed by an EXACT contactId match instead of a `q=<value>` substring
   * search — `value` is still shown in the (still-editable) search box for
   * readability, but is NOT what the feed request filters by. Substring
   * name matching collided ("Jon" matched "Jonathan") and matched
   * EVERYTHING for a nameless contact (empty seed string); id-based scoping
   * has neither failure mode.
   */
  requestsSearchSeed: { value: string; contactId?: string } | null;
  /**
   * Opens the sidebar, switches to the Requests tab, and seeds it scoped to
   * `contactId` — the "View all →" control in a contact's Recent Actions
   * section when more recent_request facts exist than the 2 rendered as
   * pills. `contactName` is display-only (shown in the search box for
   * readability); the actual feed filter is the EXACT contactId match (item
   * #7 — a name-based substring search collides between similarly-named
   * contacts and matches everything for a nameless one). Sibling to
   * `openRequestDetail` above: that one drills into ONE execution's detail;
   * this one has no single target, it just narrows the (still-editable)
   * list to the contact's own runs.
   */
  openRequestsForContact: (contactId: string, contactName: string) => void;
  /** See `ContactJumpRequest` above. */
  contactJumpRequest: ContactJumpRequest | null;
  /** Called by RequestDetail's "View on contact →" jump. */
  jumpToContact: (contactId: string) => void;
  /**
   * QA round 2 (R4) — registration point for a mounted contact table's jump
   * handler. Called from `ContactFlowTable`'s mount effect; returns an
   * unregister function for the effect's cleanup. Every mounted contact
   * table's handler is kept (a Set), not just the latest — the provider
   * tries each in registration order and treats the jump as handled on the
   * first one to report `true`. Needed since the Plays tab mounts THREE
   * contact tables at once (plan 2026-09-25-002); a single mutable slot let
   * collapsing one section's accordion (unmounting its table) clobber the
   * registration even while the other two tables — one of which might own
   * the jumped-to contact's row — stayed mounted (Codex r3 finding #3, plan
   * 2026-09-25-003 U2). This provider is mounted once at shell level (always
   * mounted, unlike any one contact table), so it's the right place to fall
   * back to a toast when no handler is registered at all — see
   * `ContactJumpRequest`'s doc comment above for the bug that fallback closes.
   */
  registerContactJumpHandler: (handler: ContactJumpHandler) => () => void;
  /**
   * The current By Play card landing target (flow-contacts-bulk-send plan,
   * U4) — the sibling of `queueFlashRequest` above, one level up: the flash
   * primitive targets a CONTACT ROW, this one targets a PLAY CARD.
   *
   * Set by an add that lands a batch on one play (U6's bulk add, U7's single
   * direct enroll). `PlayQueueCard` consumes it to (a) force its card open and
   * (b) render the "N added, M skipped" confirmation banner. `null` when no
   * landing is pending.
   *
   * Deliberately separate state rather than a field on `QueueFlashRequest`:
   * the flash request is consumed the moment its 1.6s animation completes,
   * while the banner must survive until the rep dismisses it (or a newer
   * landing replaces it). The two are requested together on the direct-enroll
   * path and share one nonce counter, so their nonces are never ambiguous.
   */
  queuePlayLanding: QueuePlayLanding | null;
  /** Sets `queuePlayLanding` (see above). Callers pair it with `openQueueTab()`. */
  requestPlayCardLanding: (target: { playTypeId: number; playInstanceRef: string; added: number; skipped: number; source: 'single' | 'bulk' }) => void;
  /**
   * Clears `queuePlayLanding` when the rep dismisses the banner. Nonce-guarded
   * for the same reason `consumeQueueFlashRequest` is: a dismiss racing a
   * newer landing must not swallow the newer one.
   */
  dismissQueuePlayLanding: (nonce: number) => void;
  /**
   * The rep's staged play-level base copy (design-fidelity plan D8 Part B) —
   * a facade over the browser-persisted `state/staged-play-config` zustand
   * store, scoped to the CURRENT effective identity. Threaded as ONE prop
   * down `RailDock` → `PlaysPane` → the play rows →
   * `BaseCopyEditor`, so no consumer knows or cares
   * where the state lives.
   *
   * `setDraft` is the WHOLE write path: there is no endpoint, no autosave and
   * no debounce-to-network for base copy any more (the play-level
   * `FlowPlayOverride.base_subject`/`base_body` columns, their PUT and the
   * server-side overlay are DELETED). zustand `persist` rides each store
   * write through to localStorage, so a staged draft survives card collapse,
   * a sidebar-rail switch, a grouping switch, a reload, a tab close and a
   * browser restart — and is structurally incapable of reaching another rep.
   * Only DIRTY drafts are stored (keyed `${playTypeId}:${playInstanceRef}`);
   * a play with no entry renders the wire's campaign copy directly, so
   * SSE/plays-refetch churn can never wipe or stale-pin a clean editor.
   */
  baseCopyDraftStore: BaseCopyDraftStore;
  /**
   * terra-ohe.7 (F1/DBOS durable-send migration U6) — REPLACES the deleted
   * client-side send-time materialization function (D8 Part B point 4).
   * Since U2, the server resolves the staged base_copy/base_sender/
   * sender_touched precedence itself when it claims each row into `queued`
   * — this function does NO resolution and writes NOTHING; it only packages
   * whatever this identity has staged for `itemIds`' plays into the wire
   * shape `POST /api/flow/queue/send`'s optional `staged` field expects
   * (`StagedSendConfigSchema`), narrowed to the batch (an unrelated play's
   * or unrelated item's staged entries never ride along).
   *
   * Returns `undefined` — never an all-empty object — when nothing staged
   * applies to `itemIds`, so callers omit `staged` from the POST body
   * (`buildStagedSendSnapshot` in `state/staged-play-config.ts`, this
   * function's pure implementation).
   *
   * There is no rollback closure any more: with no pre-POST PATCH, there is
   * nothing to compensate on a failed or skipped send. A skipped item simply
   * never had its staged values claimed onto it server-side.
   */
  buildStagedSnapshot: (itemIds: number[]) => StagedSendConfig | undefined;
  /**
   * `POST /api/flow/queue/items/bulk` (U2's route) — the Contacts tab's
   * bulk-enroll popover's single commit point (flow-contacts-bulk-send plan,
   * U6). Adds (or adopts) every eligible contact id onto ONE play in one
   * request, then — on success — invalidates the queue query AND lands the
   * play's card open with the RESPONSE's real added/skipped counts
   * (`requestPlayCardLanding` + `openQueueTab`, the same landing seam a
   * future single-contact direct-enroll path can share). Unlike
   * `addContact` (which leaves opening the sidebar to its caller), this
   * function does the landing itself — the plan's "EnrollQueueContext gains
   * the bulk-add mutation ... requestQueueFlash-style landing" line.
   *
   * Resolves with the raw `BulkAddResponse` so the caller (`BulkEnrollPopover`)
   * can decide its own post-commit UI (clearing the just-submitted selection)
   * off the real per-contact outcomes rather than a guess. REJECTS on a
   * non-2xx so the caller can show an error toast and retain the selection
   * for a retry — no invalidation and no landing fire on failure, so a
   * failed request never produces a phantom banner.
   */
  bulkAdd: (input: { contactIds: string[]; playTypeId: number; playInstanceRef: string; sourceContext?: string }) => Promise<BulkAddResponse>;
  /**
   * `POST /api/flow/queue/items/bulk-delete` (D21, flow-contacts-bulk-send QA
   * round-3 plan) — the By Play card's "Clear all N", fired ONLY from
   * `PlayQueueCard`'s confirm dialog (`GroupActionConfirmDialog`,
   * `confirmVariant="danger"`), never a direct-fire path. AWAITS the queue
   * invalidation before resolving (the `addMutation`/`bulkAdd` pattern) and
   * surfaces a toast naming the skipped count when the server reports any
   * `skipped_sending` outcome (a mid-send item survived the clear).
   */
  bulkRemove: (itemIds: number[]) => Promise<void>;
  /**
   * True when queue writes are blocked for this session (D4): impersonating
   * and writes not unlocked, in every environment. Mirrors THE RULE in
   * src/auth/write-guard.ts.
   */
  readOnly: boolean;
  /** True when the session impersonates the demo identity (`target === 'demo'`). */
  isDemo: boolean;
  /**
   * Whether Send is blocked: `sendReadOnly = readOnly && !isDemo`. Demo Send is
   * mocked server-side by the demo gate; every other write (edit, remove,
   * cancel, unstick) stays gated on `readOnly`.
   */
  sendReadOnly: boolean;
  /**
   * Why writes are off when it is not plain impersonation (the Signals "View as"
   * scope); `null` keeps each surface's impersonation copy.
   */
  readOnlyReason: string | null;
  isLoading: boolean;
  /**
   * U-B (play-catalog race guard): true once the play catalog fetch has
   * settled ONE WAY OR THE OTHER — success OR failure. Deliberately NOT
   * `playsQuery.isSuccess`: a caller that gates a UI affordance on `false ==
   * still loading` must unlock on a FAILED fetch too, or the affordance
   * dead-locks forever (disabled, no error, no retry) instead of falling
   * back to the documented unassigned-add path. `pending` is React Query's
   * v5 status value for "no data yet, fetch in flight or not yet started" —
   * `!== 'pending'` is true for both `success` and `error`.
   */
  playsResolved: boolean;
  /**
   * A send (POST + off-request delivery poll) is in flight. Send buttons
   * disable on this so a double-click / second-tab can't fire a second
   * `POST /send` (B1: the server's atomic claim is the real guard; this is
   * the UX-level belt to it). Cleared when the poll settles.
   */
  isSending: boolean;
  /**
   * POST /api/flow/queue/items — adds `contactId`. With no `playTypeId`/
   * `playInstanceRef`, the item lands unassigned (the original contract).
   * With both (2026-07-23 — the suggested-invite card's one-click "Add to
   * queue with a play"), the server assigns the play in the SAME request
   * (add + assign in one action, not add-then-PATCH) — see
   * src/services/flow/queue.ts addQueueItem. This applies whether the
   * contact is brand new OR already sitting in the queue unassigned: the
   * server compare-and-sets the play onto the existing unassigned row
   * (`maybeAssignPlayToUnassigned`) rather than silently ignoring the play
   * fields on a dedup hit. If the contact's existing item is already
   * assigned to a play (same or different), that item is left unchanged —
   * re-assigning an in-progress draft stays PATCH's job, not a side effect
   * of add. Passing only one of playTypeId/playInstanceRef is a 400 from the
   * server (M6, the same both-or-neither rule PATCH enforces).
   */
  addContact: (
    contactId: string,
    opts?: {
      sourceContext?: string;
      note?: string;
      playTypeId?: number;
      playInstanceRef?: string;
      /**
       * `salesforce_warm_intro.id` connector preselect (warm-intro plan U6,
       * Key Decision 6) — written into the add request's `warm_intro_id`
       * field in the SAME request as the add (no follow-up PATCH). Only the
       * warm-intro pill's "Add to queue" action (StatePills.tsx) ever passes
       * this; every other caller omits it and the field stays unset.
       */
      warmIntroId?: string;
    },
  ) => Promise<void>;
  /**
   * PATCH .../items/:id — assigns a play (server auto-transitions unassigned
   * -> draft).
   *
   * D12 Codex round-2 fixes #4 and #5 — this call now mirrors the SERVER's own
   * `playChanged` predicate (`src/services/flow/queue.ts`: the patch sets a
   * non-null play AND the item already had one AND the (type, ref) pair
   * actually differs) and acts on the two cases differently:
   *
   * - **A real move.** The server resets the item's drafts and send-gate
   *   overrides, so every scrap of client state authored under the OLD play is
   *   now garbage. The item's ENTIRE autosave entry is dropped — not just its
   *   `senderTouched` pin, which is all round 1 cleared. A retained
   *   `pendingPatch` (e.g. one restored by a FAILED pre-reassign flush, which
   *   `AssignedRow` deliberately swallows) or a retained `ackedPatch` could
   *   otherwise be flushed or read by a later Send and land the old play's
   *   sender/copy on the item under its NEW play. A write barrier is held for
   *   the duration so no edit made mid-reassignment survives it either.
   * - **The same play again.** `AssignedRow` deliberately does not filter the
   *   current play out of the reassign picker because picking it is a server
   *   no-op — and it must be a CLIENT no-op too. Round 1 cleared
   *   `senderTouched` on ANY successful call, so re-picking the assigned play
   *   silently un-pinned a deliberate manual sender override and made the row
   *   start following the staged global sender again.
   *
   * D12 Codex round-3 fix #3 — the call is SERIALIZED per item
   * (`useItemOpQueue`), and the `playChanged` classification above is
   * evaluated after its turn rather than at call time. The predicate reads the
   * query cache, which a still-in-flight sibling reassignment has not updated
   * yet: a rapid A→B, then (before B lands) →A sequence classified the second
   * call against a cache that still said A, computed a false "same-play
   * no-op", and skipped every side effect the real B→A move needed. Waiting a
   * turn makes the existing predicate simply correct instead of needing a
   * second, cleverer one.
   */
  assignPlay: (itemId: number, play: PlayOption) => Promise<void>;
  /**
   * PATCH .../items/:id — generic draft-field / status patch (U5). The
   * edit-in-place editor (sidebar) and the full-screen review both call
   * this against the SAME item id — D2's single draft store means there is
   * no separate per-surface write path.
   */
  patchItem: (itemId: number, patch: Partial<Pick<ItemPatch, 'sender' | 'subject_draft' | 'body_draft' | 'warm_intro_id' | 'preferred_channel' | 'status' | 'cool_off_override' | 'recent_invite_override'>>) => Promise<void>;
  /**
   * U2 (buttonless autosaving editor) — schedules a debounced draft-field
   * PATCH; replaces the removed "Save draft" button. See `AUTOSAVE_DEBOUNCE_MS`.
   *
   * D12 Codex fix, findings #1–#3: `patch` is now SPARSE (only the halves
   * actually being written) and `owned` records, per half, whether those
   * values are the rep's own deliberate choice — see `ItemAutosavePatch` and
   * `ItemAutosaveOwnership`. Every call must be CUMULATIVE for the item: a
   * later call REPLACES the pending patch wholesale (each carries the full
   * set of halves that still need writing), which is what keeps the
   * dispatch-order guarantee free of a per-field merge protocol.
   */
  autosavePatch: (itemId: number, patch: ItemAutosavePatch, owned: ItemAutosaveOwnership) => void;
  /**
   * D12 Codex fix, finding #4 — drops any autosave SCHEDULED for `itemId`
   * (the debounce timer plus its queued patch) and settles anything already
   * in flight, then forgets the entry entirely. Resolves once no write this
   * client scheduled for that item can still land.
   *
   * "Reset to base copy" is the caller: it PATCHes the row back to
   * base-following through `patchItem` — a write OUTSIDE the entry machinery
   * — so without this a sender picked moments earlier had its debounce timer
   * still ticking, fired AFTER the reset PATCH resolved, and silently
   * re-applied the discarded sender while the row's `senderTouched` pin had
   * already been cleared.
   *
   * Deliberately NOT `flushAutosave`: reset means "throw that edit away",
   * and flushing would COMMIT the very write the rep just discarded.
   *
   * D12 Codex round-2 fix, finding #3 — this alone is NOT a barrier, only a
   * drain, which is why `resetItemToBase` below now owns the whole reset
   * sequence: awaiting the in-flight write it happened to observe leaves two
   * windows open (a new edit made DURING that await gets deleted along with
   * the entry; a new edit made after the delete but before the reset's own
   * PATCH resolves creates a fresh entry whose debounce fires afterwards).
   */
  discardAutosave: (itemId: number) => Promise<void>;
  /**
   * D12 Codex round-2 fixes #2 and #3 — "Reset to base copy", as ONE
   * operation with a real barrier around it. Previously assembled at the call
   * site (`EnrollQueueSidebar.handleResetToBase`) out of `discardAutosave` +
   * `patchItem` + `baseCopyDraftStore.clearSenderTouched`, which left the
   * three steps individually correct but jointly racy.
   *
   * The sequence, and why each step is where it is:
   *
   * 1. Claim this item's WRITE BARRIER, synchronously, before the first await
   *    (#3). While held, `autosavePatch` refuses to schedule and
   *    `dispatchAutosave` refuses to dispatch for this item — so an edit made
   *    anywhere in the reset window cannot survive it, whether it lands
   *    during the in-flight await or after the entry is already gone.
   * 2. `discardAutosave` — drain and forget whatever was already queued.
   * 3. PATCH the row back to base-following (`subject_draft`/`body_draft` to
   *    null, `sender` to the caller's computed effective base — the sender
   *    has no play-level rung server-side to fall through to).
   * 4. AWAIT the queue refetch, so what the open editor is about to reseed
   *    from is the post-reset row rather than the pre-reset one.
   * 5. Clear the `senderTouched` pin and bump the item's draft-reset
   *    generation (#2), which is what makes a MOUNTED editor for this item
   *    drop its own local WRITE/OWN refs and reseed its draft. Without it the
   *    open editor kept displaying the discarded values and the rep's next
   *    keystroke re-persisted them.
   *
   * Steps 3-5 are ordered so a REJECTED PATCH leaves the row exactly as it
   * was — badge, pin, editor contents and all. The barrier releases in a
   * `finally` regardless.
   *
   * ── D12 Codex round-3 fixes #2 and #6 ────────────────────────────────────
   *
   * Steps 1-5 now run inside the per-item SERIAL OP QUEUE
   * (`useItemOpQueue`), so a reset and a concurrent reassignment of the same
   * item take turns rather than firing two unsequenced destructive PATCHes.
   * `baseSender` is computed by the CALLER against the play the row was
   * showing, so the queued op re-validates that context before writing: if
   * the item moved plays while it waited, the reset ABORTS rather than write a
   * sender belonging to a play the row is no longer in (a reassignment's own
   * server-side reset has already nulled the drafts, which is the other half
   * of what reset does).
   *
   * Step 5's generation bump also became two-phase: the reset RECORDS the
   * post-reset trio it is waiting for and the provider's settle effect bumps
   * once `groups` demonstrably carries it. `await invalidateQueries` proves
   * the query cache is current, not that React re-rendered the editor — and a
   * bump that lost that race reseeded the new generation from the PRE-reset
   * `item` prop, permanently for the sender half.
   */
  resetItemToBase: (itemId: number, baseSender: string | null) => Promise<void>;
  /**
   * Drains `itemId`'s pending/in-flight `autosavePatch` writes until
   * genuinely idle — the Send handler's required first step
   * (flush-before-send ordering, U2 behavior spec #2). Round-3: also waits
   * on a PATCH that is ALREADY in flight, and rejects (after that write
   * settles) if it fails. No-op when nothing is unsaved or in flight.
   */
  flushAutosave: (itemId: number) => Promise<void>;
  /**
   * Persists a server-resolved options field's selection (U8's connector
   * picker, and — round-2 QA feedback, Task 4 — the Preferred Channel
   * picklist) — the ONE place an input-options FIELD is mapped to the
   * `ItemPatch` column it lives in, so both editor mount surfaces (sidebar +
   * review) write it identically. `persistsTo` (the field's own discriminator
   * — see `InputFieldOptionsSchema`'s doc comment) says which column; never
   * switch on `fieldKey`, which is owned by the play type's definition file
   * server-side. `null` clears the selection.
   */
  selectInputOption: (itemId: number, fieldKey: string, persistsTo: InputFieldOptions['persistsTo'], value: string | null) => Promise<void>;
  /**
   * Fix 5a (MAJOR bd finding): on-demand staleness check for ONE item's
   * input-options cache entry, called when its editor mounts for a draft
   * warm_intro item. `staleTime` alone (15 min, Fix 5b) already covers the
   * common case via react-query's own refetch-on-stale-remount — but the
   * input-options queries are fanned out ONCE at provider level (see the
   * `useQueries` below), never remounted when an editor opens/closes, so
   * opening an editor is not itself a new subscription. This force-
   * invalidates the cached entry when it's older than the mirror's real
   * refresh cadence (`WARM_INTRO_SYNC_CADENCE_MS`, hourly — backend-owned,
   * kept in sync by hand). A no-op if the entry was never fetched or is
   * still fresh.
   */
  refreshInputOptionsIfStale: (itemId: number) => void;
  /** DELETE .../items/:id — removes the item from the queue entirely. */
  removeItem: (itemId: number) => Promise<void>;
  /**
   * POST .../items/:id/unstick — PM1 (PR #1312 pre-merge fix): resets a
   * reaper-flagged stuck row (crashed mid-send, `status: 'sending'`
   * forever) to `ready` so it's editable and re-sendable via the existing
   * Send button. The one escape hatch for a row that could previously
   * neither be deleted nor edited nor resent — manual SQL was the only way
   * out before this.
   */
  unstickItem: (itemId: number) => Promise<void>;
  /**
   * True while `unstickMutation` is in flight. GLOBAL (not per-item) by
   * design: the mutation settles in well under a second, a rep has at most
   * a handful of stuck rows at once (stuck-row is a rare, reaper-flagged
   * edge case, not a normal queue state), and a single boolean also
   * naturally prevents the same button being double-clicked before its
   * first click's request even lands — a per-item map would need the exact
   * same guard PLUS bookkeeping this doesn't need. Every "Reset to retry"
   * button (sidebar `QueueBody`, including inside `PlayQueueCard`) disables
   * on `readOnly || isUnstickPending`.
   */
  isUnstickPending: boolean;
  /**
   * terra-ohe.8 (F1/DBOS durable-send migration U6) — `POST
   * .../items/:id/cancel`: a rep's explicit cancel of a `queued` row, before
   * the DBOS workflow claims it and moves it to `sending` (past that point
   * the server rejects with `ItemNotCancelableError`, mapped to a 400 this
   * wrapper surfaces via toast). Sibling of `unstickItem` above — same shape,
   * different direction: unstick recovers a row FROM a bad state, cancel
   * removes one BEFORE it reaches a bad state.
   */
  cancelQueuedItem: (itemId: number) => Promise<void>;
  /**
   * True while `cancelMutation` is in flight. GLOBAL, same rationale as
   * `isUnstickPending` above — a rep has at most a handful of `queued` rows
   * open at once, and a per-item map would need identical bookkeeping this
   * doesn't. Every "Cancel" button (`AssignedRow`'s `ContactRowMeta`)
   * disables on `readOnly || isCancelPending`.
   */
  isCancelPending: boolean;
  /**
   * POST /api/flow/queue/send — admits one or many `ready` items (D7: single
   * "Send 1" and batch "Send N" share this one call). The endpoint returns
   * 202 with the admission result only; delivery happens off the request
   * path via `EnrollmentSender` (U6) and lands as `status: "submitted"` on
   * the next refetch, which the invalidation below triggers.
   *
   * D12 Codex round-2 fix, finding #6 — resolves with the admission RESULT
   * (`SendItemsOutcome`) rather than `void`, so a caller can tell "the server
   * refused these specific ids" apart from "everything went out".
   *
   * terra-ohe.7 (F1 U6) — the optional second argument is the batch's staged
   * wire snapshot (`buildStagedSnapshot`'s output, `StagedSendConfig`). Posted
   * verbatim as `SendSchema.staged`; omitted entirely when there is nothing
   * staged for the batch.
   */
  sendItems: (itemIds: number[], staged?: StagedSendConfig) => Promise<SendItemsOutcome>;
  /**
   * U4 behavior spec 5 — the last `POST /api/flow/queue/send` response's
   * server-authoritative skip label for `itemId`, or `null`. See
   * `sendSkipReasons`'s doc comment for the wholesale-replace semantics.
   */
  sendSkipLabelFor: (itemId: number) => string | null;
  /**
   * Basic save-and-send (plan 2026-08-19-004, D1) — records one step's FULL
   * effective Subject+Body snapshot in the LOCAL draft store. NO network:
   * the store is the only thing an edit touches until Send. Overwrites any
   * prior entry for the step (including a pending reset intent — last user
   * action wins) and clears that step's save error.
   */
  editStepOverride: (itemId: number, stepNumber: number, stepType: string, values: StepOverrideSnapshot) => void;
  /**
   * "Use flow default" — LOCAL intent until Send (Decision #4). When a
   * persisted `item.step_drafts` row exists the entry becomes `values: null`
   * and Send issues the DELETE; a purely local unsaved edit's entry is
   * simply dropped. Either way that step's save error clears.
   *
   * Adversarial follow-up (finding #3): whether a persisted row exists is
   * decided INSIDE the context — from its own record of this session's
   * successful PUTs plus the query cache's `step_drafts` — never from a
   * caller-supplied boolean. A prop derived from render-time queue data is
   * stale in the window between a PUT landing server-side and the
   * invalidation refetch, and a stale `false` silently skipped a needed
   * DELETE (the persisted override then shipped despite the visible reset).
   */
  resetStepOverrideToDefault: (itemId: number, stepNumber: number, stepType: string) => void;
  /**
   * U7b (reassign-drafts fix, round-6 adversarial follow-up on Codex #3) —
   * drops every unsaved step-override draft/save-error for `itemId`, LOCALLY
   * only (no PUT/DELETE). The reassign flow (`AssignedRow`'s "Change play…"
   * picker) calls this the moment a play change is initiated, so a stale
   * per-step edit typed under the OLD play can never survive to display
   * under — or, worse, be pushed by a later `saveStepOverrides` call onto —
   * the item's NEW play. See `clearStepOverrideDraftsForItem`'s doc comment
   * (state/step-override-drafts.ts) for why this removes entries outright
   * rather than routing through `resetStepOverrideToDefault`.
   *
   * Reflection fix (2026-09-01, failure-restore contract) — returns an undo
   * closure over the swept entries. The clear itself stays synchronous and
   * unconditional (moving it to after the reassign PATCH resolves would
   * reopen the exact stale-draft window U7b closed), but if that PATCH then
   * REJECTS the item never actually moved server-side, so the caller should
   * call the returned closure to put the swept drafts back — skipping any key
   * the rep has since retyped (editor reopened mid-flight), same
   * keystrokes-win posture as `useDraftState.reseedIfUnchanged`. Callers that
   * don't care about the failure path (there are none left after this fix,
   * but the type doesn't force handling it) can simply ignore the return
   * value.
   */
  clearStepOverridesForItem: (itemId: number) => () => void;
  /**
   * The unsaved local entry for one step (`values: null` = reset intent), or
   * `null` when untouched. Typing-lag fix (2026-08-20): referentially
   * STABLE — reads the step-override draft store directly
   * (`state/step-override-drafts.ts`), never React state — so calling it does
   * not itself trigger or depend on a re-render. A consumer that needs to
   * react to changes pairs it with `subscribeStepOverrides` (see that prop's
   * doc comment).
   */
  stepOverrideDraftFor: (itemId: number, stepNumber: number) => StepOverrideDraft | null;
  /** The last `saveStepOverrides` failure message for one step, or `null`. Cleared on edit/reset/success. Same stability note as `stepOverrideDraftFor`. */
  stepSaveErrorFor: (itemId: number, stepNumber: number) => string | null;
  /**
   * Typing-lag fix (2026-08-20) — the subscribe half for one
   * `${itemId}:${stepNumber}` entry (both the draft AND its save error share
   * this one subscription). `StepOverrideRow` (StepOverrideEditor.tsx) is the
   * one consumer: it pairs this with `stepOverrideDraftFor` /
   * `stepSaveErrorFor` through `useStepOverrideEntry` (zustand's `useStore`
   * over the store module's per-key subscription) so an edit re-renders
   * exactly that row — not sibling steps, not other items, not the
   * sidebar/list — instead of the old design where `editStepOverride` went
   * through `setStepOverrideDrafts` (React state) and every context consumer
   * re-rendered on every keystroke.
   */
  subscribeStepOverrides: (itemId: number, stepNumber: number, onChange: () => void) => () => void;
  /**
   * The ONE write moment for step overrides (D2): PUTs every locally-edited
   * step (full snapshot) and DELETEs every reset step for `itemId`, in
   * parallel via `Promise.allSettled` — it never settles while any sibling
   * write is in flight (the F4/P1-GROUP lesson). On full success the item's
   * entries drop. On ANY failure: every entry is kept (retry = Send again),
   * per-step errors are recorded, and the FIRST failure's error is thrown —
   * a rejection means "do not send". No-op when nothing is unsaved. Must be
   * awaited before the `ready`/`sending` flip, or the server's
   * `assertOwnedEditableItem` rejects with `STEP_DRAFT_ITEM_IMMUTABLE` —
   * the sequential Send handler guarantees that by construction.
   */
  saveStepOverrides: (itemId: number) => Promise<void>;
  /**
   * Adversarial follow-up (findings 1/2/5) — the per-item send-pipeline
   * lock. True while ANY surface's flush→save→ready→send pipeline is
   * running for `itemId`. Every surface that renders the item derives its
   * field lock and its Remove/Close disables from this, so the lock is
   * visible in EVERY mounted instance — not just the one that initiated the
   * send (the old guard was a ref/state pair local to one editor instance,
   * so a collapse+reopen or a different trigger surface started unlocked).
   *
   * D12 Codex round-3 fix #1 — "busy" now also covers a running DESTRUCTIVE
   * OPERATION (`resetItemToBase`, `assignPlay`), not only a send pipeline.
   * Both rewrite the row's server state from under every surface, and round
   * 2's write barrier that covered them was invisible: the Send button stayed
   * clickable straight through a reset. Composed from the two underlying sets
   * (`useSendLock`'s `busyItems`, `useItemOpQueue`'s `opPendingItems`) so
   * consumers keep asking exactly ONE question — "can I touch this row?"
   */
  isItemBusy: (itemId: number) => boolean;
  /**
   * Synchronously claims the pipeline lock for every id — or refuses
   * (returns false and claims NOTHING) when any of them is already
   * mid-pipeline on any surface. Every send entry point calls this before
   * its first await and releases with `endSendPipeline` in a `finally`.
   * A presence/absence flag only — deliberately NOT a queue, scheduler, or
   * drain loop (the machinery plan 004 removed).
   *
   * D12 Codex round-3 fix #1 — it ALSO refuses when a destructive per-item
   * operation is queued or running for any of the ids
   * (`useItemOpQueue.isItemOpPending`, a synchronous ref read layered ahead of
   * the lock's own check-and-set). Without it, a Send fired during a
   * reset/reassignment window sailed past the write barrier — which only
   * suppressed the WRITES — and POSTed the item with whatever the server
   * currently held rather than the staged copy/sender the UI was showing.
   */
  beginSendPipeline: (itemIds: number[]) => boolean;
  /** Releases the pipeline lock for every id — the pipeline's unconditional `finally`. */
  endSendPipeline: (itemIds: number[]) => void;
  /**
   * D17 (flow-contacts-bulk-send QA round-3 plan) — exposes `useItemOpQueue`'s
   * `runItemOp` to the sidebar for `handleSendAnyway`'s merged unlock-then-send
   * (Q1: "yeah merge anyway"): the unlock PATCH runs through this so it is
   * genuinely SEQUENCED against any other destructive op already queued for
   * the same item (`resetItemToBase`/`assignPlay`, this hook's only other two
   * producers) — the same guarantee they already get from each other. See
   * `useItemOpQueue`'s module doc comment for the full contract; every other
   * context member composes on top of it rather than reaching it directly,
   * but the sidebar's merged handler IS itself a destructive per-item
   * operation, so it needs the primitive, not just its derived `isItemBusy`.
   */
  runItemOp: <T>(itemId: number, op: () => Promise<T>) => Promise<T>;
  /**
   * Companion to `runItemOp` above — see `useItemOpQueue`'s doc comment.
   * `runItemOp`'s OWN returned promise settles as soon as `op` resolves, one
   * microtask BEFORE the hook's internal bookkeeping (the depth decrement
   * `isItemOpPending`/`beginSendPipeline` read off) actually runs — a caller
   * that claims the send pipeline synchronously right after `await
   * runItemOp(...)` would see its own just-finished op as still pending and
   * get refused. `awaitItemOps` genuinely waits that bookkeeping out;
   * `handleSendAnyway` calls it between the unlock PATCH and claiming the
   * send pipeline for exactly this reason.
   */
  awaitItemOps: (itemId: number) => Promise<void>;
}

/**
 * Fix 5a (MAJOR bd finding): carries the HTTP status alongside the message so
 * a caller can distinguish "the picked warm-intro connector vanished
 * server-side" (400 — `patchQueueItem`'s `InvalidPatchError` for an unknown
 * `warm_intro_id`, the mirror's delete-absent pass having removed the row
 * between page-load and click) from any other PATCH failure (network error,
 * 500, a genuinely different 400). `selectInputOption` below is the ONE
 * caller that acts on `.status` specifically.
 */
export class PatchQueueItemError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'PatchQueueItemError';
  }
}

/**
 * The provider's three real-time wall-clock delays, injectable so a test can
 * run the same real code paths without paying the production waits.
 *
 * Why a PROP on `EnrollQueueProvider` and not a module-level constant gated
 * on `NODE_ENV`: a module global is shared by every test file in a vitest
 * worker, so whichever file mutated it last wins and the suite becomes
 * order-dependent. A prop is per-mount, so two providers in one file (the
 * store-isolation test mounts exactly that) can hold different values with no
 * interference.
 *
 * These are the only three delays that gate an ASSERTION in the queue's test
 * suites. Deliberately NOT included: the 30s+ post-send poll deadline
 * (`Date.now() + 30_000 + n * 600` in `sendMutation.onSuccess`) and the
 * contact-state invalidation coalescing timer — no test waits either one out,
 * so injecting them would be unused surface.
 */
export interface EnrollQueueTimers {
  /**
   * Trailing-edge debounce for the item editor's autosave PATCH — the
   * production value is `AUTOSAVE_DEBOUNCE_MS` (see `useItemAutosave.ts`).
   */
  autosaveDebounceMs: number;
  /** Delay before the post-send delivery poll's FIRST `GET /api/flow/queue`. */
  sendPollInitialMs: number;
  /** Delay between subsequent post-send delivery polls. */
  sendPollIntervalMs: number;
}
